import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import logoBlack from 'assets/crafter-logo-black.png';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { useKnowledgeBaseContactForm } from './KnowledgeBaseContactForm';
import { fetchPublicKnowledgeBase } from './knowledgeBaseApi';
import { KnowledgeBaseArticleListItem, KNOWLEDGE_BASE_PUBLIC_PATH } from './KnowledgeBaseTypes';
import '../../style/scss/default-elements.scss';
import '../../style/scss/button.scss';
import '../../style/scss/knowledge-base.scss';

interface KnowledgeBaseLayoutProps {
    children: React.ReactNode;
    hero?: boolean;
    width?: 'wide' | 'narrow';
}

// Shows the public knowledge base frame with the header, the search bar and the centered content
const KnowledgeBaseLayout: React.FC<KnowledgeBaseLayoutProps> = ({ children, hero = false, width = 'narrow' }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<KnowledgeBaseArticleListItem[]>([]);
    const [showResults, setShowResults] = useState(false);
    const [searching, setSearching] = useState(false);
    const openContactForm = useKnowledgeBaseContactForm();
    const searchRef = useRef<HTMLDivElement>(null);

    // Search the knowledge base a moment after the last keystroke
    useEffect(() => {
        const term = search.trim();
        if (term.length < 2) { setResults([]); setSearching(false); return; }
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const data = await fetchPublicKnowledgeBase<{ results: KnowledgeBaseArticleListItem[] }>('get_public_knowledge_base_search', { search: term });
                setResults(data.results);
                setShowResults(true);
            } catch (error) {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Close the results when clicking outside the search bar
    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) setShowResults(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Navigates to a search result
    const openResult = (article: KnowledgeBaseArticleListItem) => {
        setShowResults(false);
        setSearch('');
        history.push(`${KNOWLEDGE_BASE_PUBLIC_PATH}/artikel/${article.id}`);
    };

    // The search bar with its result list
    const searchBar = (
        <div className={`kb-search ${hero ? 'kb-search-hero' : ''}`} ref={searchRef}>
            <div className='kb-search-field'>
                <FontAwesomeIcon icon={faMagnifyingGlass} className='kb-search-icon' />
                <input
                    type='text'
                    value={search}
                    placeholder={t('knowledge_base.public.search_placeholder')}
                    onChange={(event) => setSearch(event.target.value)}
                    onFocus={() => { if (results.length > 0) setShowResults(true); }} />
                {search && (
                    <button type='button' className='kb-search-clear' onClick={() => { setSearch(''); setResults([]); }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>
            {showResults && search.trim().length >= 2 && (
                <div className='kb-search-results'>
                    {results.length === 0 && !searching && <div className='kb-search-empty'>{t('knowledge_base.public.search_no_results')}</div>}
                    {results.map(article => (
                        <div key={article.id} className='kb-search-result' onClick={() => openResult(article)}>
                            <div className='kb-search-result-title'>{article.subject}</div>
                            <div className='kb-search-result-meta'>
                                {article.group_name}{article.description ? ` · ${article.description}` : ''}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className='kb-public'>
            <header className={`kb-header ${hero ? 'kb-header-hero' : ''}`}>
                <div className={`kb-header-inner kb-header-inner-${width}`}>
                    <Link to={KNOWLEDGE_BASE_PUBLIC_PATH} className='kb-logo'>
                        <img src={logoBlack} alt='Crafter' />
                        <span>{t('knowledge_base.public.title')}</span>
                    </Link>
                    <div className='kb-header-tools'>
                        {!hero && searchBar}
                        <PrimaryButton label='knowledge_base.contact.button' size='small' onClick={openContactForm} />
                    </div>
                </div>
                {hero && (
                    <div className='kb-hero'>
                        <h1>{t('knowledge_base.public.hero_title')}</h1>
                        <p>{t('knowledge_base.public.hero_subtitle')}</p>
                        {searchBar}
                    </div>
                )}
            </header>
            <main className={`kb-content kb-content-${width}`}>
                {children}
            </main>
        </div>
    );
};

export default KnowledgeBaseLayout;
